import { useState } from 'react'
import { useApiKeys, useCreateApiKey, usePlatforms, useRevokeApiKey } from '../api/hooks'
import type { ApiKeyCreated, ApiKeyScope } from '../api/types'
import { Modal } from '../components/Modal'
import { useUi } from '../store/ui'

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export function ApiKeysTab() {
  const keys = useApiKeys()
  const platforms = usePlatforms()
  const revoke = useRevokeApiKey()
  const pushToast = useUi((state) => state.pushToast)
  const [showCreate, setShowCreate] = useState(false)
  const [created, setCreated] = useState<ApiKeyCreated | null>(null)

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-ink-600">
          Machine keys for the pull/push API (<code className="font-mono">/api/v1/*</code>). Scoped
          read or write, optionally pinned to one platform.
        </p>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          + Create key
        </button>
      </div>

      {keys.isError && (
        <p role="alert" className="rounded border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-900">
          Could not load API keys: {(keys.error as Error).message}
        </p>
      )}

      <div className="overflow-hidden rounded-lg border border-ink-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-ink-100 text-2xs uppercase tracking-wider text-ink-600">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Name</th>
              <th className="px-3 py-2 text-left font-semibold">Key</th>
              <th className="px-3 py-2 text-left font-semibold">Scope</th>
              <th className="px-3 py-2 text-left font-semibold">Platform</th>
              <th className="px-3 py-2 text-left font-semibold">Created</th>
              <th className="px-3 py-2 text-left font-semibold">Status</th>
              <th className="px-3 py-2 text-left font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {(keys.data ?? []).map((key) => (
              <tr key={key.id} className={key.revoked_at ? 'bg-ink-50/60 text-ink-600' : ''}>
                <td className="px-3 py-2">{key.name}</td>
                <td className="px-3 py-2 font-mono text-ink-700">{key.key_prefix}…</td>
                <td className="px-3 py-2">
                  <span className="chip border-ink-300 bg-white text-ink-700">{key.scope}</span>
                </td>
                <td className="px-3 py-2 font-mono text-2xs">{key.platform ?? 'all platforms'}</td>
                <td className="px-3 py-2 text-ink-600">{formatDate(key.created_at)}</td>
                <td className="px-3 py-2">
                  {key.revoked_at ? (
                    <span className="chip border-ink-300 bg-ink-100 text-ink-600">Revoked</span>
                  ) : (
                    <span className="chip border-emerald-300 bg-emerald-50 text-emerald-800">Active</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {!key.revoked_at && (
                    <button
                      className="btn btn-danger"
                      disabled={revoke.isPending}
                      onClick={() => {
                        if (!window.confirm(`Revoke "${key.name}"? Anything using it stops working immediately.`))
                          return
                        revoke.mutate(key.id, {
                          onSuccess: () => pushToast('success', `"${key.name}" revoked.`),
                          onError: (error) => pushToast('error', `Revoke failed: ${(error as Error).message}`),
                        })
                      }}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {keys.isSuccess && keys.data.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-center text-ink-600">
                  No API keys yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateApiKeyDialog
          platforms={(platforms.data ?? []).map((platform) => platform.slug)}
          onClose={() => setShowCreate(false)}
          onCreated={(key) => {
            setShowCreate(false)
            setCreated(key)
          }}
        />
      )}
      {created && <ApiKeyRevealDialog created={created} onClose={() => setCreated(null)} />}
    </div>
  )
}

function CreateApiKeyDialog({
  platforms,
  onClose,
  onCreated,
}: {
  platforms: string[]
  onClose: () => void
  onCreated: (key: ApiKeyCreated) => void
}) {
  const create = useCreateApiKey()
  const [name, setName] = useState('')
  const [scope, setScope] = useState<ApiKeyScope>('read')
  const [platform, setPlatform] = useState<string>('')

  return (
    <Modal
      title="Create API key"
      description="The full key is shown once, immediately after creation, and never again."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={!name.trim() || create.isPending}
            onClick={() =>
              create.mutate(
                { name: name.trim(), scope, platform: platform || null },
                { onSuccess: onCreated },
              )
            }
          >
            {create.isPending ? 'Creating…' : 'Create key'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="label" htmlFor="api-key-name">
            Name
          </label>
          <input
            id="api-key-name"
            className="field mt-1"
            placeholder="ci-pipeline"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <fieldset>
          <legend className="label">Scope</legend>
          <div className="mt-1 flex gap-3">
            {(['read', 'write'] as ApiKeyScope[]).map((option) => (
              <label key={option} className="flex items-center gap-1.5 text-xs text-ink-700">
                <input
                  type="radio"
                  name="api-key-scope"
                  checked={scope === option}
                  onChange={() => setScope(option)}
                />
                {option === 'read' ? 'Read (pull only)' : 'Write (pull + push)'}
              </label>
            ))}
          </div>
        </fieldset>
        <div>
          <label className="label" htmlFor="api-key-platform">
            Pin to platform (optional)
          </label>
          <select
            id="api-key-platform"
            className="field mt-1"
            value={platform}
            onChange={(event) => setPlatform(event.target.value)}
          >
            <option value="">All platforms</option>
            {platforms.map((slug) => (
              <option key={slug} value={slug}>
                {slug}
              </option>
            ))}
          </select>
        </div>
        {create.isError && (
          <p role="alert" className="rounded border border-rose-300 bg-rose-50 px-2 py-1.5 text-xs text-rose-900">
            {(create.error as Error).message}
          </p>
        )}
      </div>
    </Modal>
  )
}

function ApiKeyRevealDialog({ created, onClose }: { created: ApiKeyCreated; onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  return (
    <Modal
      title="API key created"
      description={`"${created.name}" — ${created.scope} scope${created.platform ? `, pinned to ${created.platform}` : ''}.`}
      onClose={onClose}
      footer={
        <button className="btn btn-primary" onClick={onClose}>
          Done — I've saved it
        </button>
      }
    >
      <div className="space-y-2">
        <p role="alert" className="rounded border border-amber-400 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
          <strong>This key cannot be retrieved again.</strong> Copy it now and store it somewhere
          safe (a secrets manager, not a chat message). Only its prefix is shown from now on.
        </p>
        <div className="flex items-center gap-2">
          <code className="field flex-1 overflow-x-auto whitespace-nowrap font-mono text-xs">
            {created.key}
          </code>
          <button
            className="btn"
            onClick={() => {
              navigator.clipboard?.writeText(created.key)
              setCopied(true)
            }}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
